import './style.css';
import { Vec2, makeLine } from '@lakaran/geometry';
import { serializeDocument } from '@lakaran/engine';
import { CadRenderer, isWebGpuAvailable } from './render/renderer';
import { CadEditor } from './editor/editor';
import { attachInput } from './input';
import { createDimensionEditor } from './dimension-editor';
import { createUi } from './ui';
import { installNodePanel } from '../nodes/index';
import { installFileLaunch } from './launch';
import { kernel } from './workers/pool';
import { detectBrowser, webGpuGuidance } from './caps';
import { suppressBrowserContextMenu } from './context-menu';

function showFatal(title: string, detail: string): void {
  const el = document.getElementById('stage-fatal');
  if (!el) return;
  el.hidden = false;
  el.innerHTML = '';
  const heading = document.createElement('h2');
  heading.textContent = title;
  const body = document.createElement('p');
  body.innerHTML = detail;
  el.append(heading, body);
}

async function boot(): Promise<void> {
  const host = document.getElementById('viewport');
  if (!host) throw new Error('Missing #viewport element');

  // Registered before the WebGPU probe: the worker has to be up for the app to be installable and
  // for the fatal panel below to render offline.
  registerServiceWorker();

  if (!(await isWebGpuAvailable())) {
    showFatal('WebGPU is required', webGpuGuidance(detectBrowser()));
    return;
  }

  const editor = new CadEditor();

  let renderer: CadRenderer;
  try {
    renderer = await CadRenderer.create(host, editor);
  } catch (error) {
    showFatal('WebGPU renderer failed to start', String(error));
    return;
  }

  if (renderer.backend !== 'webgpu') {
    renderer.destroy();
    showFatal('WebGPU renderer unavailable', webGpuGuidance(detectBrowser()));
    return;
  }

  const nodes = installNodePanel(editor, renderer);
  const files = createUi(editor, renderer, nodes?.store ?? null);
  attachInput(renderer.canvasElement, editor);
  createDimensionEditor(editor, document.getElementById('viewport') ?? document.body);
  // The browser menu has nothing useful on a drawing canvas; the app draws its own.
  suppressBrowserContextMenu();
  editor.zoomExtents();

  // Heavy kernel work (constraint sweeps on dense models, DXF/PDF writing) runs in workers.
  editor.offloadSolve = async (payload) => {
    const response = await kernel.run({ kind: 'solve', ...payload });
    if (response.kind !== 'solve') throw new Error('unexpected kernel response');
    return response;
  };

  // Opening a `.lakar` from the OS launches the installed app with the file attached.
  const launchReady = installFileLaunch(files);

  Object.assign(window as unknown as Record<string, unknown>, {
    __lk: { editor, renderer, files, kernel, launchReady, bench: runBench },
  });
}

/** Dev utility: `__lk.bench(20000)` reports export timings for a synthetic drawing. */
async function runBench(entityCount = 20000): Promise<Record<string, unknown>> {
  const editor = (window as unknown as { __lk: { editor: CadEditor } }).__lk.editor;
  const doc = editor.doc;
  for (let index = 0; index < entityCount; index++) {
    const x = (index % 200) * 12;
    const y = Math.floor(index / 200) * 12;
    doc.add(makeLine(Vec2.of(x, y), Vec2.of(x + 10, y + 6), doc.currentLayerId));
  }
  const serializeStart = performance.now();
  const json = serializeDocument(doc);
  const serializeMs = performance.now() - serializeStart;

  const workerStart = performance.now();
  const response = await kernel.run({ kind: 'export', format: 'dxf', json });
  const workerMs = performance.now() - workerStart;

  return {
    entities: doc.entityCount,
    bytes: typeof response === 'object' && response.kind === 'export' ? response.data.length : 0,
    serializeMs: Math.round(serializeMs),
    exportMs: Math.round(workerMs),
    workers: kernel.usesWorkers ? kernel.workerCount : 0,
  };
}

function registerServiceWorker(): void {
  // The worker caches the shell, so it stays out of the Vite dev server and HMR.
  if (!import.meta.env.PROD || !('serviceWorker' in navigator)) return;
  const register = (): void => {
    void navigator.serviceWorker.register('/sw.js').catch((error: unknown) => {
      console.warn('Lakaran: service worker registration failed', error);
    });
  };
  // Boot awaits WebGPU, so `load` may already have fired by the time we get here.
  if (document.readyState === 'complete') register();
  else window.addEventListener('load', register, { once: true });
}

void boot();
